import { ref, update, onValue, onDisconnect, serverTimestamp } from 'firebase/database';
import { rtdb } from './firebase';
import { RTDB_PATHS } from '../utils/constants';
import { withErrorHandling, ERROR_TYPES, createError, logError } from '../utils/errorHandling';

/**
 * Set a user as online in the presence system
 * @param {string} userId - User ID
 * @param {string} displayName - User's display name
 * @param {string} color - User's assigned color
 * @returns {Promise<Object>} Result with success status
 */
export async function setUserOnline(userId, displayName, color) {
  return await withErrorHandling(
    async () => {
      // Validate inputs
      if (!userId || !displayName) {
        throw createError(
          'User ID and display name are required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const userSessionPath = RTDB_PATHS.getUserSession(userId);
      const userRef = ref(rtdb, userSessionPath);
      
      // Set user as online and update presence data
      await update(userRef, {
        userId,
        displayName,
        cursorColor: color || '#666666',
        isOnline: true,
        lastSeen: serverTimestamp(),
        joinedAt: serverTimestamp(),
        aiActive: false,
        currentAIPrompt: null
      });
      
      // Set up automatic offline status on disconnect
      await onDisconnect(userRef).update({
        isOnline: false,
        lastSeen: serverTimestamp()
      });
      
      console.log('✅ User set to online:', displayName);
      return {
        success: true,
        message: `${displayName} is now online`
      };
    },
    'Set User Online',
    { 
      errorType: ERROR_TYPES.NETWORK,
      enableRetry: true,
      enableOfflineQueue: false // Presence should be immediate
    }
  );
}

/**
 * Manually set a user as offline
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Result with success status
 */
export async function setUserOffline(userId) {
  return await withErrorHandling(
    async () => {
      // Validate input
      if (!userId) {
        throw createError(
          'User ID is required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const userSessionPath = RTDB_PATHS.getUserSession(userId);
      const userRef = ref(rtdb, userSessionPath);
      
      // Set user as offline
      await update(userRef, {
        isOnline: false,
        lastSeen: serverTimestamp()
      });
      
      console.log('📴 User set to offline:', userId);
      return {
        success: true,
        message: 'User set to offline'
      };
    },
    'Set User Offline',
    { 
      errorType: ERROR_TYPES.NETWORK,
      enableRetry: true,
      enableOfflineQueue: false // Presence should be immediate
    }
  );
}

/**
 * Subscribe to presence updates for all users
 * @param {Function} callback - Function to call with online users array
 * @returns {Function} unsubscribe function
 */
export function subscribeToPresence(callback) {
  try {
    const sessionsRef = ref(rtdb, RTDB_PATHS.USER_SESSIONS);
    
    const unsubscribe = onValue(sessionsRef, 
      (snapshot) => {
        try {
          const onlineUsers = [];
          
          if (snapshot.exists()) {
            const allSessions = snapshot.val();
            
            console.log('🔍 DEBUG - Raw presence data:', {
              totalSessions: Object.keys(allSessions).length,
              sessions: Object.entries(allSessions).map(([userId, userData]) => ({
                userId: userId.substring(0, 8) + '...',
                displayName: userData?.displayName,
                isOnline: userData?.isOnline,
                hasData: !!userData
              }))
            });
            
            // Filter for online users only
            Object.entries(allSessions).forEach(([userId, userData]) => {
              const isOnline = userData && userData.isOnline === true;
              
              console.log(`🔍 DEBUG - User ${userId.substring(0, 8)}...: isOnline=${userData?.isOnline}, displayName=${userData?.displayName}`);
              
              if (isOnline) {
                onlineUsers.push({
                  userId,
                  displayName: userData.displayName || 'Anonymous',
                  cursorColor: userData.cursorColor || '#666666',
                  cursorX: userData.cursorX || 0,
                  cursorY: userData.cursorY || 0,
                  lastSeen: userData.lastSeen || Date.now(),
                  joinedAt: userData.joinedAt || Date.now(),
                  isOnline: true,
                  aiActive: userData.aiStatus?.aiActive || false,
                  currentAIPrompt: userData.aiStatus?.currentPrompt || null
                });
              }
            });
          }
          
          console.log('👥 Presence updated:', {
            count: onlineUsers.length,
            users: onlineUsers.map(u => ({
              id: u.userId.substring(0, 8) + '...',
              name: u.displayName
            }))
          });
          
          callback(onlineUsers);
        } catch (error) {
          logError(createError(error, ERROR_TYPES.NETWORK, 'medium'), 'Presence subscription callback');
          callback([]);
        }
      }, 
      (error) => {
        logError(createError(error, ERROR_TYPES.NETWORK, 'high'), 'Presence subscription listener');
        // Call callback with empty array on error
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    logError(createError(error, ERROR_TYPES.NETWORK, 'high'), 'Setting up presence subscription');
    // Return no-op function if setup fails
    return () => {};
  }
}

/**
 * Get current online users count (one-time read)
 * @returns {Promise<number>} Number of online users
 */
export async function getOnlineUsersCount() {
  try {
    const sessionsRef = ref(rtdb, RTDB_PATHS.USER_SESSIONS);
    
    return new Promise((resolve, reject) => {
      onValue(sessionsRef, (snapshot) => {
        let count = 0;
        
        if (snapshot.exists()) {
          const allSessions = snapshot.val();
          Object.values(allSessions).forEach(userData => {
            if (userData && userData.isOnline === true) {
              count++;
            }
          });
        }
        
        resolve(count);
      }, { onlyOnce: true });
    });
  } catch (error) {
    console.error('❌ Error getting online users count:', error);
    return 0;
  }
}

/**
 * Update user's last seen timestamp
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export async function updateLastSeen(userId) {
  try {
    const userSessionPath = RTDB_PATHS.getUserSession(userId);
    const userRef = ref(rtdb, userSessionPath);
    
    await update(userRef, {
      lastSeen: serverTimestamp()
    });
  } catch (error) {
    console.error('❌ Error updating last seen:', error);
    // Don't throw - this is a background operation
  }
}

/**
 * Check if a specific user is online
 * @param {string} userId - User ID to check
 * @returns {Promise<boolean>} Whether the user is online
 */
export async function isUserOnline(userId) {
  try {
    const userSessionPath = RTDB_PATHS.getUserSession(userId);
    const userRef = ref(rtdb, userSessionPath);
    
    return new Promise((resolve) => {
      onValue(userRef, (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.val();
          resolve(userData.isOnline === true);
        } else {
          resolve(false);
        }
      }, { onlyOnce: true });
    });
  } catch (error) {
    console.error('❌ Error checking user online status:', error);
    return false;
  }
}

/**
 * Initialize presence for a user (combines online status and cleanup)
 * @param {string} userId - User ID
 * @param {string} displayName - User's display name
 * @param {string} color - User's assigned color
 * @returns {Promise<void>}
 */
export async function initializePresence(userId, displayName, color) {
  try {
    await setUserOnline(userId, displayName, color);
    console.log('🟢 Presence initialized for:', displayName);
  } catch (error) {
    console.error('❌ Error initializing presence:', error);
    throw error;
  }
}

/**
 * Clean up presence on logout or component unmount
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export async function cleanupPresence(userId) {
  try {
    await setUserOffline(userId);
    console.log('🔴 Presence cleaned up for user:', userId);
  } catch (error) {
    console.error('❌ Error cleaning up presence:', error);
    // Don't throw - cleanup should be non-blocking
  }
}

/**
 * Set user's AI activity status
 * @param {string} userId - User ID
 * @param {boolean} isActive - Whether AI is active
 * @param {string} prompt - Current AI prompt (optional)
 * @returns {Promise<void>}
 */
export async function setUserAIActive(userId, isActive, prompt = null) {
  try {
    if (!userId) {
      throw createError(
        'User ID is required',
        ERROR_TYPES.VALIDATION,
        'medium'
      );
    }

    const userSessionPath = RTDB_PATHS.getUserSession(userId);
    const userRef = ref(rtdb, `${userSessionPath}/aiStatus`);
    
    if (isActive) {
      await update(userRef, {
        aiActive: true,
        currentPrompt: prompt || 'Generating shapes...',
        startedAt: Date.now()
      });
      console.log('✨ User AI active:', userId, prompt);
    } else {
      await update(userRef, {
        aiActive: false,
        currentPrompt: null,
        startedAt: null
      });
      console.log('✨ User AI inactive:', userId);
    }
  } catch (error) {
    console.error('❌ Error setting AI activity:', error);
    // Don't throw - this is a background operation
  }
}

/**
 * Set user's AI inactive (convenience function)
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export async function setUserAIInactive(userId) {
  return setUserAIActive(userId, false);
}
